/**
 * McpManagePage — self-service key portal (magic link).
 * Route: /mcp/manage[?token=...]
 * Without token: request a management link by email.
 * With token: usage overview + key rotation (single use, 30 min TTL).
 */
import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { KeyRound, Copy, Check, RefreshCw, AlertTriangle, BarChart3, ShieldAlert } from "lucide-react";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { API } from "../config";

const tierColor = (tier) => (tier === "admin" ? "#f59e0b" : tier === "pro" ? "#a855f7" : "#22d3ee");

const RequestLinkForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/mcp/manage/request-link`, { email: email.trim() });
      setSent(data.message);
    } catch (err) {
      setError(err?.response?.status === 429
        ? "Too many requests — try again in an hour."
        : "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="manage-link-sent" style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 14, padding: 20 }}>
        <p style={{ margin: 0, fontSize: 14, color: "#34d399", fontWeight: 600 }}>{sent}</p>
        <p style={{ margin: "10px 0 0", fontSize: 12, color: "#64748b" }}>The link works once for rotation and expires after 30 minutes.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} data-testid="manage-email-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
        placeholder="Email you registered your key with"
        data-testid="manage-email-input"
        style={{ background: "#1a2122", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "13px 16px", fontSize: 14, color: "#e2e8f0", outline: "none" }}
      />
      {error && <p data-testid="manage-form-error" style={{ margin: 0, fontSize: 13, color: "#f87171" }}>{error}</p>}
      <button
        type="submit" disabled={busy}
        data-testid="manage-request-link-btn"
        style={{ background: "#22d3ee", color: "#001f24", border: "none", borderRadius: 12, padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}
      >
        {busy ? "Sending…" : "Email me a management link"}
      </button>
      <p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>Max 3 requests per hour per network. We never show your key — only usage stats and rotation.</p>
    </form>
  );
};

const UsageChart = ({ usage }) => {
  const days = usage?.days || [];
  const max = Math.max(1, ...days.map((d) => usage.daily[d] || 0));
  return (
    <div data-testid="manage-usage-chart">
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 56 }}>
        {days.map((d) => {
          const v = usage.daily[d] || 0;
          return (
            <div key={d} title={`${d}: ${v} request${v === 1 ? "" : "s"}`}
              style={{ flex: 1, borderRadius: 3, height: v === 0 ? 3 : Math.max(6, Math.round((v / max) * 56)), background: v === 0 ? "rgba(255,255,255,0.08)" : "#22d3ee" }} />
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: "#64748b", fontFamily: "ui-monospace, monospace" }}>
        <span>{days[0]}</span>
        <span>{days[days.length - 1]}</span>
      </div>
    </div>
  );
};

export default function McpManagePage() {
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token") || "";
  const [overview, setOverview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotated, setRotated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [betaBusy, setBetaBusy] = useState(false);
  const [betaDone, setBetaDone] = useState(null);
  const [betaError, setBetaError] = useState(null);

  useEffect(() => { document.title = "Manage your MCP key — TourismIntel"; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mcp/manage/overview`, { params: { token } });
      setOverview(data);
    } catch (err) {
      setLoadError(err?.response?.data?.detail || "Invalid or expired link — request a new one below.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const rotate = async () => {
    if (!confirmRotate) { setConfirmRotate(true); return; }
    setRotating(true);
    try {
      const { data } = await axios.post(`${API}/mcp/manage/rotate`, { token });
      setRotated(data);
    } catch (err) {
      setLoadError(err?.response?.data?.detail || "Rotation failed — the link may have expired.");
    } finally {
      setRotating(false);
      setConfirmRotate(false);
    }
  };

  const requestBeta = async () => {
    setBetaBusy(true);
    setBetaError(null);
    try {
      const { data } = await axios.post(`${API}/mcp/request-pro-beta`, { manage_token: token });
      setBetaDone(data.message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setBetaError(typeof detail === "string" ? detail : "Something went wrong — please try again.");
    } finally {
      setBetaBusy(false);
    }
  };

  const copyNewKey = async () => {
    try {
      await navigator.clipboard.writeText(rotated.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const key = overview?.key;
  const color = tierColor(key?.tier);

  return (
    <div style={{ background: "#0c1011", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif", display: "flex", flexDirection: "column" }}>
      <PublicPageNav active="mcp" />
      <main style={{ flex: 1, padding: "56px 24px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <KeyRound style={{ width: 18, height: 18, color: "#22d3ee" }} />
            <h1 data-testid="manage-page-title" style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Reckless Neue Variable', serif" }}>
              Manage your MCP key
            </h1>
          </div>

          {!token && (
            <>
              <p style={{ margin: "0 0 24px", fontSize: 14, color: "#94a3b8", lineHeight: 1.6 }}>
                Enter the email you registered your key with and we&apos;ll send you a secure,
                single-use management link — check your usage and rotate your key if needed.
              </p>
              <RequestLinkForm />
            </>
          )}

          {token && loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
              <RefreshCw style={{ width: 20, height: 20, color: "#22d3ee", animation: "spin 1s linear infinite" }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {token && loadError && !rotated && (
            <div style={{ marginTop: 8 }}>
              <div data-testid="manage-error" style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 14, padding: 16, marginBottom: 24 }}>
                <ShieldAlert style={{ width: 16, height: 16, color: "#f87171", flexShrink: 0, marginTop: 2 }} />
                <p style={{ margin: 0, fontSize: 13, color: "#f87171" }}>{loadError}</p>
              </div>
              <RequestLinkForm />
            </div>
          )}

          {rotated && (
            <div data-testid="manage-rotated" style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.3)", borderRadius: 14, padding: 20 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                <AlertTriangle style={{ width: 16, height: 16, color: "#fbbf24", flexShrink: 0, marginTop: 2 }} />
                <p style={{ margin: 0, fontSize: 13, color: "#fbbf24", fontWeight: 600 }}>
                  Key rotated. Save the new key now — the old one is already invalid.
                  {rotated.email_sent ? " A copy was emailed to you." : ""}
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#0c1011", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 14px" }}>
                <code data-testid="manage-new-key" style={{ flex: 1, fontSize: 13, color: "#22d3ee", wordBreak: "break-all", fontFamily: "ui-monospace, monospace" }}>{rotated.key}</code>
                <button onClick={copyNewKey} data-testid="manage-copy-new-key-btn" aria-label="Copy new key" style={{ background: "none", border: "none", cursor: "pointer", color: copied ? "#34d399" : "#94a3b8", padding: 6 }}>
                  {copied ? <Check style={{ width: 16, height: 16 }} /> : <Copy style={{ width: 16, height: 16 }} />}
                </button>
              </div>
              <p style={{ margin: "14px 0 0", fontSize: 12, color: "#64748b" }}>
                Update the <code style={{ color: "#94a3b8" }}>Authorization: Bearer</code> header (or the <code style={{ color: "#94a3b8" }}>x-api-key</code> header of your Claude.ai connector) in your MCP client config. Tier, quota and usage history were preserved.
              </p>
            </div>
          )}

          {overview && !rotated && (
            <div data-testid="manage-overview" style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
              {/* Key card */}
              <div style={{ background: "#1a2122", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span data-testid="manage-key-name" style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>{key.name}</span>
                  <span data-testid="manage-key-tier" style={{ padding: "2px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", background: `${color}22`, color }}>{key.tier}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginTop: 14, fontSize: 12 }}>
                  <div><span style={{ color: "#64748b" }}>Key</span><br /><code style={{ color: "#94a3b8", fontFamily: "ui-monospace, monospace" }}>{key.key_prefix}…</code></div>
                  <div><span style={{ color: "#64748b" }}>Owner</span><br /><span style={{ color: "#94a3b8" }}>{key.email}</span></div>
                  <div><span style={{ color: "#64748b" }}>Created</span><br /><span style={{ color: "#94a3b8" }}>{key.created_at ? new Date(key.created_at).toLocaleDateString() : "—"}</span></div>
                  <div><span style={{ color: "#64748b" }}>Last used</span><br /><span style={{ color: "#94a3b8" }}>{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : "never"}</span></div>
                </div>
                {overview.limits && (
                  <p style={{ margin: "14px 0 0", fontSize: 11, color: "#64748b" }}>Rate limits: {overview.limits}</p>
                )}
              </div>

              {/* Usage */}
              <div style={{ background: "#1a2122", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>
                    <BarChart3 style={{ width: 14, height: 14, color: "#22d3ee" }} /> Usage — last 14 days
                  </span>
                  <span data-testid="manage-usage-total" style={{ fontSize: 12, color: "#94a3b8", fontFamily: "ui-monospace, monospace" }}>{overview.usage.total} requests</span>
                </div>
                <UsageChart usage={overview.usage} />
              </div>

              {/* Ask quota (PRO) */}
              {overview.ask_quota && (
                <div data-testid="manage-ask-quota" style={{ background: "#1a2122", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>ask_tourismintel quota (this month)</span>
                    <span style={{ fontSize: 12, color: "#a855f7", fontFamily: "ui-monospace, monospace" }}>{overview.ask_quota.used} / {overview.ask_quota.limit}</span>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 999, height: 8, overflow: "hidden" }}>
                    <div style={{ background: "#a855f7", height: 8, width: `${Math.min(100, (overview.ask_quota.used / overview.ask_quota.limit) * 100)}%` }} />
                  </div>
                  {overview.pro_plan?.is_pro && (
                    <p data-testid="manage-pro-linked" style={{ margin: "12px 0 0", fontSize: 11, color: "#64748b" }}>
                      ✓ Linked to your TourismIntel PRO plan ({key.email})
                    </p>
                  )}
                </div>
              )}

              {/* Upgrade to PRO (open tier) — payments not live yet, Beta handled manually */}
              {key.tier === "open" && (
                <div data-testid="manage-upgrade-box" style={{ background: "rgba(168,85,247,0.06)", border: "1px solid rgba(168,85,247,0.3)", borderRadius: 14, padding: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>Unlock ask_tourismintel with PRO</p>
                    <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", background: "rgba(168,85,247,0.15)", color: "#c4b5fd" }}>
                      Payments coming soon
                    </span>
                  </div>
                  <p style={{ margin: "0 0 14px", fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
                    MCP PRO is part of the <strong style={{ color: "#c4b5fd" }}>TourismIntel PRO</strong> plan
                    (web + mobile + MCP): cited RAG answers from the archive, 100 questions/month,
                    60 req/min · 2,000/day. While web payments launch, we grant <strong style={{ color: "#c4b5fd" }}>Beta PRO access manually</strong> —
                    request it below and we&apos;ll get back to <strong style={{ color: "#c4b5fd" }}>{key.email}</strong>.
                  </p>
                  {betaDone ? (
                    <p data-testid="manage-beta-success" style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#34d399" }}>✓ {betaDone}</p>
                  ) : (
                    <>
                      {betaError && <p data-testid="manage-beta-error" style={{ margin: "0 0 10px", fontSize: 12, color: "#f87171" }}>{betaError}</p>}
                      <button
                        onClick={requestBeta} disabled={betaBusy}
                        data-testid="manage-upgrade-cta"
                        style={{ background: "#a855f7", color: "#fff", border: "none", cursor: betaBusy ? "wait" : "pointer", borderRadius: 999, padding: "10px 22px", fontSize: 13, fontWeight: 700, opacity: betaBusy ? 0.7 : 1 }}
                      >
                        {betaBusy ? "Sending…" : "Request Beta PRO access"}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Rotate */}
              <div style={{ background: "rgba(248,113,113,0.05)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 14, padding: 20 }}>
                <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>Rotate this key</p>
                <p style={{ margin: "0 0 14px", fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
                  Generates a new secret and invalidates the current key immediately.
                  Tier, quotas and usage history are preserved. Use this if your key leaked.
                </p>
                <button
                  onClick={rotate} disabled={rotating}
                  data-testid="manage-rotate-btn"
                  style={{ background: confirmRotate ? "#f87171" : "transparent", color: confirmRotate ? "#0c1011" : "#f87171", border: "1px solid rgba(248,113,113,0.5)", borderRadius: 999, padding: "10px 22px", fontSize: 13, fontWeight: 700, cursor: rotating ? "wait" : "pointer" }}
                >
                  {rotating ? "Rotating…" : confirmRotate ? "Click again to confirm — old key dies now" : "Rotate key"}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <PublicPageFooter />
    </div>
  );
}
